import React, { useMemo, useState } from 'react';
import { Select } from 'antd';
import { getTextWidth } from '../../../utils';

export type DynamicPicklistColumn = {
  id: string;
  options: Array<{
    label: string;
    value: string;
    filters?: {
      [columnId: string]: string;
    };
  }>;
};

export interface Props {
  columns: Array<DynamicPicklistColumn>;
  value?: Record<string, string>;
  onValueChange?: (value: Record<string, string> | undefined) => void | Promise<void>;
  disabled?: boolean;
  resetValuesOnChange?: boolean;
}

const NO_SELECTION_TEXT = 'Select...';

const SegmentedPicklist = ({
  columns,
  value,
  onValueChange,
  disabled,
  resetValuesOnChange,
}: Props) => {
  const selectWidthByColumn = useMemo(
    () => columns.reduce((acc, { id, options }) => {
      const font = '.875rem Inter,sans-serif';
      const widths = options.map(o => getTextWidth(o.label, font));

      // Include border of <Select/>
      const width = Math.max(50, ...widths) + 50;

      return { ...acc, [id]: width };
    }, {}),
    [columns],
  );

  const parsedSelections = value ?? {};

  const [
    tempSelections,
    setTempSelections,
  ] = useState<{ [columnId: string]: string }>(parsedSelections);

  const filterColumnItemBySelections = (
    filters?: { [key: string]: string },
    selections?: { [columnId: string]: string },
  ): boolean => {
    const filterKeys = Object.keys(filters || {});
    if (!filters || !filterKeys.length) {
      return true;
    }
    for (let i = 0; i < filterKeys.length; i += 1) {
      const key = filterKeys[i];
      if (selections?.[key] !== filters[key]) {
        return false;
      }
    }
    return true;
  };

  const getPickerOptions = (selections: { [columnId: string]: string }) => columns.map((col) => {
    const filteredOptions = col.options
      .filter(o => filterColumnItemBySelections(o.filters, selections))
      .map(o => ({
        label: o.label,
        value: o.value,
      }));

    return {
      key: col.id,
      items: filteredOptions,
    };
  });

  const pickerOptions = getPickerOptions(tempSelections);

  const onValueChangeHandler = (columnId: string, selectedValue: string) => {
    // On change, reset the index of all the columns to the right
    const newTempSelections = { ...tempSelections };
    if (resetValuesOnChange && tempSelections[columnId] !== selectedValue) {
      const eventIndex = columns.findIndex(column => column.id === columnId);
      const columnsToReset = columns.slice(eventIndex + 1);
      columnsToReset.forEach((column) => {
        delete newTempSelections[column.id];
      });
    }

    const selections = {
      ...newTempSelections,
      [columnId]: selectedValue,
    };

    setTempSelections(selections);

    const updatedPickerOptions = getPickerOptions(selections);

    const selectionsAreValid = updatedPickerOptions.every(
      ({ key, items }) => items
        .map(i => i.value)
        .includes(selections[key]),
    );
    if (selectionsAreValid) {
      onValueChange?.(selections);
    } else {
      // Incomplete selection is equivalent to no selection as the selection is invalid
      onValueChange?.(undefined);
    }
  };

  const renderSelects = () => pickerOptions.map(({ key, items }) => {
    if (items.length === 0) {
      const DISABLED = '-';
      return (
        <Select
          key={key}
          value={DISABLED}
          options={[
            {
              label: DISABLED,
              value: DISABLED,
            },
          ]}
          disabled
          style={{ width: selectWidthByColumn[key] }}
        />
      );
    }

    const selectedInOptions = items.some(i => i.value === tempSelections[key]);

    // We intentionally put NO_SELECTION_TEXT as a value as <Select/> will
    // show the text directly if the value is not a valid option.
    const processedValue = selectedInOptions ? tempSelections[key] : NO_SELECTION_TEXT;
    return (
      <Select
        key={key}
        value={processedValue}
        onChange={selectedValue => onValueChangeHandler(key, selectedValue)}
        options={items}
        disabled={disabled}
        style={{ width: selectWidthByColumn[key] }}
      />
    );
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>{renderSelects()}</div>
  );
};

export default SegmentedPicklist;
